import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AgendaItem } from '@/types';
import { buildMonthCells, sameDay, addMonths } from '@/lib/dateUtils';

interface Props {
  cursor: Date;
  byDay: Record<string, AgendaItem[]>;
  onCursorChange?: (d: Date) => void;
  onDayClick?: (d: Date) => void;
}

const DOW = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export function MiniCalendar({ cursor, byDay, onCursorChange, onDayClick }: Props) {
  const cells = buildMonthCells(cursor);
  const today = new Date();
  const label = cursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <div className="rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-3.5 shadow-sm-warm dark:shadow-sm-dark">
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[12px] font-semibold text-ink-title dark:text-ink-title-dark capitalize">
          {label}
        </div>
        <div className="flex gap-0.5">
          <button
            type="button"
            onClick={() => onCursorChange?.(addMonths(cursor, -1))}
            className="w-5 h-5 rounded inline-flex items-center justify-center hover:bg-surface-alt dark:hover:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark"
            aria-label="Mês anterior"
          >
            <ChevronLeft size={12} />
          </button>
          <button
            type="button"
            onClick={() => onCursorChange?.(addMonths(cursor, 1))}
            className="w-5 h-5 rounded inline-flex items-center justify-center hover:bg-surface-alt dark:hover:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark"
            aria-label="Próximo mês"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      </div>

      {/* Dias da semana */}
      <div className="grid grid-cols-7 gap-0.5">
        {DOW.map((d, i) => (
          <div key={`h${i}`} className="text-center pb-1 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-ink-placeholder dark:text-ink-placeholder-dark">
            {d}
          </div>
        ))}
        {cells.map((c, i) => {
          const hasEv = !c.out && !!byDay[c.date.toDateString()];
          const isToday = !c.out && sameDay(c.date, today);
          return (
            <button
              key={i}
              type="button"
              onClick={() => !c.out && onDayClick?.(c.date)}
              className={[
                'relative aspect-square flex items-center justify-center rounded-md text-[11px] tabular-nums transition-colors',
                isToday
                  ? 'bg-brand-500 text-white font-semibold'
                  : c.out
                    ? 'text-ink-placeholder dark:text-ink-placeholder-dark'
                    : 'text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
              ].join(' ')}
            >
              {c.date.getDate()}
              {hasEv && (
                <span
                  className={[
                    'absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full',
                    isToday ? 'bg-white' : 'bg-brand-500',
                  ].join(' ')}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
